import { useEffect, useState } from 'react';
import { Box, Card, useForm, withStyles } from 'vopay';
import { Button, UtilButton } from '@/components/UI';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@/providers/Permissions.provider';
import { MyAccountFormData } from '../types';
import { useWithdrawAccountFields } from './useWithdrawAccountFields';
import styles from '@/styles/PaymentAndCollection/PaymentAndCollection.module.scss';

const cn = withStyles(styles);

type WithdrawAccountFormProps = {
  defaultValues: MyAccountFormData;
  onSubmit: (values: MyAccountFormData) => void;
  onCurrencyChange?: (currency: string) => void;
  hasBankAccount: boolean;
};

export default function WithdrawAccountForm({
  defaultValues,
  onSubmit,
  onCurrencyChange,
  hasBankAccount,
}: WithdrawAccountFormProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.WithdrawMyAccount.Form' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global.Buttons' });
  const { permissions } = usePermissions();
  const [isUnderReview, setIsUnderReview] = useState(false);
  const [moreInfoNeeded, setMoreInfoNeeded] = useState(false);

  const { accountFields, amountFields, scheduleFields, additionalFields } = useWithdrawAccountFields();
  const allFields = [...accountFields, ...amountFields, ...scheduleFields, ...additionalFields];

  const { renderFormElements, ctx } = useForm<MyAccountFormData, MyAccountFormData>({
    config: allFields,
    initialValues: defaultValues,
    onSubmit: (serializedData) => {
      onSubmit(serializedData);
    },
    isReviewing: isUnderReview,
  });

  useEffect(() => {
    if (defaultValues.Account && defaultValues.Account !== ctx.values.Account) {
      ctx.setFieldValue('Account', defaultValues.Account);
    }
  }, [defaultValues.Account]);

  useEffect(() => {
    if (defaultValues.ClientAccount && defaultValues.ClientAccount !== ctx.values.ClientAccount) {
      ctx.setFieldValue('ClientAccount', defaultValues.ClientAccount);
    }
  }, [defaultValues.ClientAccount]);

  useEffect(() => {
    if (ctx.values.Currency && onCurrencyChange) {
      onCurrencyChange(ctx.values.Currency);
    }
  }, [ctx.values.Currency]);

  const accountElements = renderFormElements({ config: accountFields });
  const amountElements = renderFormElements({ config: amountFields });
  const scheduleElements = renderFormElements({ config: scheduleFields });
  const additionalElements = renderFormElements({ config: additionalFields });

  return (
    <form
      onSubmit={(e) => {
        if (!isUnderReview) {
          setIsUnderReview(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          ctx.handleSubmit(e);
        }
        e.preventDefault();
      }}
      className={cn('form')}
    >
      <div className={cn('formContainer')}>
        <Box flx="col" className={cn('mainContent')}>
          <Card className={cn('card')}>
            <div className={cn('grid')}>{accountElements}</div>
            <div className={cn('grid')} style={{ marginTop: '32px' }}>
              <div className={cn('currencyFeild')}>{amountElements}</div>
            </div>
            <div className={cn('grid')} style={{ marginTop: '32px' }}>{scheduleElements}</div>

            {!moreInfoNeeded && !isUnderReview && (
              <UtilButton
                size="large"
                iconLeft="add"
                onClick={() => setMoreInfoNeeded(true)}
                disabled={isUnderReview}
                className={cn('showMoreButton')}
              >
                {t('Buttons.addMoreInformation', { defaultValue: 'Add More Information' })}
              </UtilButton>
            )}
            {(moreInfoNeeded || isUnderReview) && (
              <>
                <UtilButton
                  size="large"
                  iconLeft="remove"
                  onClick={() => setMoreInfoNeeded(false)}
                  disabled={isUnderReview}
                  className={cn('showMoreButton')}
                >
                  {t('Buttons.hideAdditionalInformation', { defaultValue: 'Hide Additional Information' })}
                </UtilButton>
                <div className={cn('grid', 'additionalInformationContainer')}>{additionalElements}</div>
              </>
            )}
          </Card>

          <Box jc="fe" pt="md" gapSize="12px">
            {isUnderReview && (
              <Button id="withdraw-edit" colorScheme="secondary" onClick={() => setIsUnderReview(false)}>
                {tGlobal('edit')}
              </Button>
            )}
            <Button
              id="withdraw-submit"
              disabled={!ctx.isValid || !hasBankAccount || !permissions.CreateWithdrawMyAccountTransactions}
              colorScheme="primary"
              type="submit"
            >
              {isUnderReview ? tGlobal('submit') : tGlobal('review')}
            </Button>
          </Box>
        </Box>
      </div>
    </form>
  );
}
